
import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, Clock, Share2, Tag, ChevronRight, Facebook, Twitter, Linkedin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';

import { BlogPost, getBlogBySlug, getAllPublishedBlogPosts } from '@/services/blog';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toast } from '@/hooks/use-toast';
import { Skeleton } from "@/components/ui/skeleton";
import { encodeS3Url } from "@/components/encodeS3Url";
import { capitalizeWords, toSentenceCase } from '@/helper';
import SEO from '@/components/seo/seo';

interface RecentBlogCardProps {
    post: BlogPost;
}

const RecentBlogCard = ({ post }: RecentBlogCardProps) => {
    return (
        <Link to={`/blogs/${post.slug}`} className="group block">
            <div className="flex gap-4 items-start">
                <div className="relative flex-shrink-0 w-24 h-24 overflow-hidden bg-muted">
                    <img
                        src={post.featuredImage ? encodeS3Url(post.featuredImage) : "/placeholder.svg"}
                        alt={post.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                </div>
                <div className="flex-grow space-y-2">
                    <h4 className="text-sm font-bold text-foreground leading-snug group-hover:text-primary transition-colors line-clamp-2 tracking-relaxed">
                        {capitalizeWords(post.title)}
                    </h4>
                    {post.excerpt && (
                        <p className="text-[11px] text-muted-foreground line-clamp-1 leading-normal">
                            {post.excerpt}
                        </p>
                    )}
                    <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                        <div className="flex items-center gap-1.5">
                            {/* <Avatar className="h-5 w-5">
                                <AvatarImage src={`https://ui-avatars.com/api/?name=${post.author}&background=random&size=32`} />
                                <AvatarFallback>{post.author.charAt(0)}</AvatarFallback>
                            </Avatar> */}
                            <span className="font-medium text-foreground/80">{capitalizeWords(post.author)}</span>
                        </div>
                        <span className="opacity-40">•</span>
                        <span>
                            {post.publishDate
                                ? new Date(post.publishDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                                : 'Recent'}
                        </span>
                        <span className="opacity-40">|</span>
                        <span className="font-semibold text-primary/90 uppercase tracking-tight text-[10px]">
                            {post.category}
                        </span>
                    </div>
                </div>
            </div>
        </Link>
    );
};

const BlogDetail = () => {
    const { slug } = useParams<{ slug: string }>();
    const [post, setPost] = useState<BlogPost | null>(null);
    const [recentPosts, setRecentPosts] = useState<BlogPost[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchPostAndRecent = async () => {
            if (!slug) return;
            try {
                // Fetch current post
                const currentPost = await getBlogBySlug(slug);
                setPost(currentPost);

                // Fetch recent posts
                const { data } = await getAllPublishedBlogPosts({ page: 1, limit: 5 });
                if (Array.isArray(data)) {
                    // Filter out the current post
                    const filtered = data.filter(p => p.slug !== slug).slice(0, 4);
                    setRecentPosts(filtered);
                }
            } catch (err) {
                console.error("Error fetching data:", err);
            } finally {
                setIsLoading(false);
            }
        };

        fetchPostAndRecent();
    }, [slug]);

    if (isLoading) {
        return (
            <>
                <div className="min-h-screen flex flex-col bg-background">
                    <main className="flex-grow pt-24 pb-16">
                        {/* Breadcrumb Skeleton */}
                        <div className="container mx-auto px-4 py-6">
                            <div className="max-w-7xl mx-auto">
                                <Skeleton className="h-4 w-64 mb-6" />
                            </div>
                        </div>

                        <div className="container mx-auto px-4">
                            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                                {/* Left Column: Content */}
                                <div className="lg:col-span-8 space-y-10">
                                    <div className="space-y-4">
                                        <Skeleton className="h-4 w-24" />
                                        <Skeleton className="h-10 md:h-14 w-full" />
                                        <Skeleton className="h-[1px] w-full" />

                                        <div className="flex items-center gap-3 py-1">
                                            <Skeleton className="h-9 w-9 rounded-full" />
                                            <Skeleton className="h-4 w-48" />
                                        </div>

                                        <Skeleton className="h-[1px] w-full" />
                                        <div className="space-y-2">
                                            <Skeleton className="h-4 w-full" />
                                            <Skeleton className="h-4 w-full" />
                                            <Skeleton className="h-4 w-2/3" />
                                        </div>

                                        <Skeleton className="h-[1px] w-full mt-8 mb-2" />
                                        <div className="flex gap-3">
                                            <Skeleton className="h-8 w-8 rounded-full" />
                                            <Skeleton className="h-8 w-8 rounded-full" />
                                            <Skeleton className="h-8 w-8 rounded-full" />
                                        </div>
                                    </div>

                                    {/* Featured Image Skeleton */}
                                    <Skeleton className="aspect-video lg:aspect-[21/9] w-full" />

                                    {/* Content Skeleton */}
                                    <div className="space-y-4">
                                        <Skeleton className="h-4 w-full" />
                                        <Skeleton className="h-4 w-full" />
                                        <Skeleton className="h-4 w-full" />
                                        <Skeleton className="h-4 w-3/4" />
                                    </div>
                                </div>

                                {/* Sidebar Skeleton */}
                                <div className="lg:col-span-4 space-y-10">
                                    <div className="space-y-6">
                                        <Skeleton className="h-8 w-48 border-b pb-4" />
                                        <div className="space-y-6">
                                            {[1, 2, 3].map((i) => (
                                                <div key={i} className="flex gap-4">
                                                    <Skeleton className="w-24 h-24 flex-shrink-0" />
                                                    <div className="flex-grow space-y-2">
                                                        <Skeleton className="h-4 w-full" />
                                                        <Skeleton className="h-3 w-2/3" />
                                                        <Skeleton className="h-3 w-1/2" />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </main>
                </div>
            </>
        );
    }


    if (!post) {
        return (
            <div className="flex-1 flex flex-col items-center justify-center pt-32 px-4 text-center">
                <h1 className="text-2xl font-bold mb-4">Post Not Found</h1>
                <p className="text-muted-foreground mb-8">The blog post you are looking for does not exist.</p>
                <Link to="/blogs">
                    <Button>Back to Blog</Button>
                </Link>
            </div>

        );
    }

    // Calculate read time (rough estimate)
    const wordCount = post.contentHTML?.split(/\s+/).length || 0;
    const readTime = Math.ceil(wordCount / 200);

    return (
        <>
            <SEO
                title={`${toSentenceCase(post.metaTitle || post.title)} | Urban Oasis UAE`}
                description={post.metaDescription}
                url={post.slug}
                image={post.featuredImage}
                type="article"
                keywords={post.tags?.join(", ")}
                author={post.author}
            />
            <div className="pt-24 pb-16">
                {/* Breadcrumb & Back Link */}
                <div className="container mx-auto px-4 py-6">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
                        <Link to="/" className="hover:text-primary transition-colors">Home</Link>
                        <ChevronRight className="h-4 w-4" />
                        <Link to="/blogs" className="hover:text-primary transition-colors">Blog</Link>
                        <ChevronRight className="h-4 w-4" />
                        <span className="text-foreground font-medium truncate max-w-[200px]">{capitalizeWords(post.title)}</span>
                    </div>
                </div>

                <div className="container mx-auto px-4">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                        {/* Left Column: Hero Text + Content */}
                        <div className="lg:col-span-8 space-y-10">
                            <div className="space-y-4">
                                <div className="flex items-center gap-2">
                                    <Badge variant="secondary" className="px-2.5 py-0.5 text-[11px] font-semibold bg-primary/5 text-primary border-primary/10 hover:bg-primary/10 transition-colors uppercase tracking-wider">
                                        {capitalizeWords(post.category)}
                                    </Badge>
                                </div>
                                <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold leading-[1.2] tracking-tight">
                                    {capitalizeWords(post.title)}
                                </h1>

                                <div className="w-full h-[1px] bg-gray-200" />

                                <div className="flex items-center gap-3 py-1">
                                    <Avatar className="h-9 w-9">
                                        <AvatarImage src={`https://ui-avatars.com/api/?name=${post.author}&background=random&size=128`} />
                                        <AvatarFallback>{post.author.charAt(0)}</AvatarFallback>
                                    </Avatar>

                                    <div className="flex items-center gap-1.5 text-[14px] text-[#555555]">
                                        <span className="opacity-80">Written by</span>
                                        <span className="font-semibold text-[#333333] underline decoration-[#333333]/30 underline-offset-[3px] decoration-1 hover:text-primary transition-colors cursor-pointer">
                                            {capitalizeWords(post.author)}
                                        </span>
                                        <span className="mx-1 text-gray-300">|</span>
                                        <span className="opacity-80">
                                            {post.publishDate
                                                ? new Date(post.publishDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
                                                : (post.createdAt ? new Date(post.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'Unknown Date')
                                            }
                                        </span>
                                    </div>
                                </div>

                                <div className="w-full h-[1px] bg-gray-200" />

                                {post.excerpt && (
                                    <p className="leading-relaxed border-primary/20 ">
                                        {toSentenceCase(post.excerpt)}
                                    </p>
                                )}

                                <div className="w-full h-[1px] bg-gray-200 mt-8 mb-2" />

                                <div className="flex items-center gap-3">
                                    <span className="text-[12px] font-medium text-[#999999] uppercase tracking-wider mr-1">Share</span>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8 rounded-full hover:bg-black hover:text-white transition-all"
                                        onClick={() => window.open(`https://twitter.com/intent/tweet?text=${post.title}&url=${window.location.href}`, '_blank')}
                                    >
                                        <Twitter className="h-3.5 w-3.5" />
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8 rounded-full hover:bg-blue-600 hover:text-white transition-all"
                                        onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${window.location.href}`, '_blank')}
                                    >
                                        <Facebook className="h-3.5 w-3.5" />
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8 rounded-full hover:bg-blue-700 hover:text-white transition-all"
                                        onClick={() => window.open(`https://www.linkedin.com/shareArticle?mini=true&url=${window.location.href}&title=${post.title}`, '_blank')}
                                    >
                                        <Linkedin className="h-3.5 w-3.5" />
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8 rounded-full hover:bg-primary hover:text-white transition-all"
                                        onClick={() => {
                                            navigator.clipboard.writeText(window.location.href);
                                            toast({
                                                title: "Link Copied",
                                                description: "Blog link copied to clipboard.",
                                            });
                                        }}
                                    >
                                        <Share2 className="h-3.5 w-3.5" />
                                    </Button>
                                </div>
                            </div>

                            {/* Featured Image Moved Here */}
                            {post.featuredImage && (
                                <div className="mb-10 overflow-hidden shadow-2xl ring-1 ring-black/5 aspect-[16/9] lg:aspect-[21/9] relative group">
                                    <img
                                        src={encodeS3Url(post.featuredImage)}
                                        alt={capitalizeWords(post.title)}
                                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                    />
                                </div>
                            )}

                            <div className="prose dark:prose-invert max-w-none">
                                <div
                                    className="text-foreground/90 text-lg leading-relaxed [&_img]:aspect-[16/9] [&_img]:lg:aspect-[21/9] [&_img]:object-cover [&_img]:rounded-none [&_img]:my-10"
                                    dangerouslySetInnerHTML={{ __html: post.contentHTML }}
                                />
                            </div>

                            {/* Tags */}
                            {post.tags && post.tags.length > 0 && (
                                <div className="mt-12 pt-8">
                                    <div className="flex items-center gap-3 mb-4">
                                        <Tag className="h-5 w-5 text-primary" />
                                        <h3 className="text-lg font-semibold">Related Topics</h3>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        {post.tags.map((tag, index) => (
                                            <Badge key={index} variant="outline" className="px-4 py-2 text-sm hover:bg-secondary transition-colors cursor-pointer">
                                                #{capitalizeWords(tag)}
                                            </Badge>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Sidebar Column */}
                        <div className="lg:col-span-4 lg:sticky lg:top-24 h-fit space-y-10">
                            {/* Recent Blogs Section */}
                            {recentPosts.length > 0 && (
                                <div className="space-y-6">
                                    <div className="flex items-center justify-between border-b pb-4">
                                        <h3 className="text-xl font-bold text-foreground">Recent Posts</h3>
                                        <Link to="/blogs" className="text-sm font-medium text-primary hover:underline">View All</Link>
                                    </div>
                                    <div className="grid gap-6">
                                        {recentPosts.map((recentPost) => (
                                            <RecentBlogCard key={recentPost._id} post={recentPost} />
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Optional: Add other sidebar elements here like newsletter or categories */}
                        </div>

                    </div>
                </div>
            </div>
        </>
    );
};

export default BlogDetail;
